// JobCoachProfitabilityReport.jsx - Profitability analysis by job coach
import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const JobCoachProfitabilityReport = () => {
  const { user } = useContext(AuthContext);

  const tenant_id = user?.tenant_id;

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    user_id: "",
  });
  const [users, setUsers] = useState([]);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [expandedConsumers, setExpandedConsumers] = useState(new Set());

  // Toggle user expansion
  const toggleUserExpansion = (userId) => {
    setExpandedUsers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
        // Also collapse all consumers under this user
        setExpandedConsumers((prevConsumers) => {
          const newConsumerSet = new Set(prevConsumers);
          reportData.forEach((user) => {
            if (user.user_id === userId && user.consumers) {
              user.consumers.forEach((c) => {
                newConsumerSet.delete(`${userId}-${c.consumer_id}`);
              });
            }
          });
          return newConsumerSet;
        });
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Toggle consumer expansion
  const toggleConsumerExpansion = (userId, consumerId) => {
    const key = `${userId}-${consumerId}`;
    setExpandedConsumers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Expand/collapse all
  const toggleAllExpanded = () => {
    if (expandedUsers.size === reportData.length) {
      setExpandedUsers(new Set());
      setExpandedConsumers(new Set());
    } else {
      const allUserIds = new Set(reportData.map((u) => u.user_id));
      setExpandedUsers(allUserIds);
    }
  };

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const userRes = await secureAxios.get("/api/users", {
          params: { tenant_id },
        });
        const jobCoaches = (userRes.data || []).filter(
          (u) =>
            u.role?.toLowerCase() === "job coach" ||
            u.role?.toLowerCase() === "admin"
        );
        setUsers(jobCoaches);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    if (tenant_id) loadFilterOptions();
  }, [tenant_id]);

  // Load report data
  const loadReport = useCallback(async () => {
    if (!tenant_id) return;
    setLoading(true);
    try {
      const response = await secureAxios.get(
        "/api/business-reports/job-coach-profitability",
        {
          params: {
            tenant_id,
            start_date: filters.start_date || undefined,
            end_date: filters.end_date || undefined,
            user_id: filters.user_id || undefined,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummary(response.data.summary || null);
        setExpandedUsers(new Set());
        setExpandedConsumers(new Set());

        await auditApi.logEntry({
          action: "GENERATE_JOB_COACH_PROFITABILITY_REPORT",
          entity_type: "REPORT",
          entity_id: null,
          contains_phi: true,
          action_status: "SUCCESS",
          purpose_of_use: "OPERATIONS",
        });
      }
    } catch (err) {
      console.error("Error loading job coach profitability report:", err);
      toast.error("Failed to load job coach profitability report");
    } finally {
      setLoading(false);
    }
  }, [tenant_id, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const formatNumber = (num, decimals = 1) => {
    return Number(num || 0).toFixed(decimals);
  };

  const getMarginColor = (margin) => {
    if (margin >= 30) return "success";
    if (margin >= 15) return "info";
    if (margin >= 0) return "warning";
    return "danger";
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-person-badge me-2"></i>
            Job Coach Profitability Report
          </h2>
          <p className="text-muted mb-0">
            Analyze profitability and utilization by job coach
          </p>
        </div>
        <button
          className="btn btn-outline-primary"
          onClick={loadReport}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Job Coach</label>
              <select
                className="form-select"
                value={filters.user_id}
                onChange={(e) =>
                  setFilters({ ...filters, user_id: e.target.value })
                }
              >
                <option value="">All Job Coaches</option>
                {users.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.start_date}
                onChange={(e) =>
                  setFilters({ ...filters, start_date: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.end_date}
                onChange={(e) =>
                  setFilters({ ...filters, end_date: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() =>
                  setFilters({ start_date: "", end_date: "", user_id: "" })
                }
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md">
            <div className="card bg-secondary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Total Hours
                </h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.total_hours, 0)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-danger text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Total Cost
                </h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.total_cost)}
                </h4>
                <small className="d-block mt-1 opacity-75">
                  <span title="Paid to employees">
                    <i className="bi bi-check-circle me-1"></i>
                    {formatCurrency(summary.cost_paid || 0)}
                  </span>
                  <span className="mx-1">/</span>
                  <span title="Not yet paid">
                    <i className="bi bi-clock me-1"></i>
                    {formatCurrency(summary.cost_unpaid || 0)}
                  </span>
                </small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-primary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Total Revenue
                </h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.total_revenue)}
                </h4>
                <small className="d-block mt-1 opacity-75">
                  <span title="Invoiced to customer">
                    <i className="bi bi-receipt me-1"></i>
                    {formatCurrency(summary.revenue_invoiced || 0)}
                  </span>
                  <span className="mx-1">/</span>
                  <span title="Not yet invoiced">
                    <i className="bi bi-receipt-cutoff me-1"></i>
                    {formatCurrency(summary.revenue_not_invoiced || 0)}
                  </span>
                </small>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div
              className={`card bg-${
                summary.total_profit >= 0 ? "success" : "danger"
              } text-white h-100`}
            >
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Total Profit
                </h6>
                <h4 className="card-title mb-0">
                  {formatCurrency(summary.total_profit)}
                </h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div
              className={`card bg-${getMarginColor(
                summary.avg_margin
              )} text-white h-100`}
            >
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Avg Margin
                </h6>
                <h4 className="card-title mb-0">
                  {formatNumber(summary.avg_margin, 1)}%
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Data Table */}
      {!loading && reportData.length > 0 && (
        <div className="card">
          {/* Expand/Collapse All button */}
          <div className="card-header bg-light py-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={toggleAllExpanded}
            >
              <i
                className={`bi bi-${
                  expandedUsers.size === reportData.length
                    ? "dash-square"
                    : "plus-square"
                } me-1`}
              ></i>
              {expandedUsers.size === reportData.length
                ? "Collapse All"
                : "Expand All"}
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th style={{ width: "40px" }}></th>
                  <th>Job Coach</th>
                  <th className="text-end">Hours</th>
                  <th className="text-end">Mileage</th>
                  <th className="text-end">Hourly Rate</th>
                  <th className="text-end">Cost</th>
                  <th className="text-end">Revenue</th>
                  <th className="text-end">Profit</th>
                  <th className="text-end">Margin</th>
                  <th className="text-center">Consumers</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row) => {
                  const isUserExpanded = expandedUsers.has(row.user_id);
                  const hasConsumers =
                    row.consumers && row.consumers.length > 0;

                  return (
                    <React.Fragment key={row.user_id}>
                      {/* Job Coach Row */}
                      <tr
                        className={hasConsumers ? "cursor-pointer" : ""}
                        onClick={() =>
                          hasConsumers && toggleUserExpansion(row.user_id)
                        }
                        style={hasConsumers ? { cursor: "pointer" } : {}}
                      >
                        <td className="text-center">
                          {hasConsumers && (
                            <i
                              className={`bi bi-chevron-${
                                isUserExpanded ? "down" : "right"
                              } text-muted`}
                            ></i>
                          )}
                        </td>
                        <td className="fw-bold">{row.user_name}</td>
                        <td className="text-end">
                          {formatNumber(row.total_hours, 1)}
                        </td>
                        <td className="text-end">
                          {formatNumber(row.total_mileage, 0)}
                        </td>
                        <td className="text-end">
                          {formatCurrency(row.hourly_rate)}
                        </td>
                        <td className="text-end text-danger">
                          {formatCurrency(row.total_cost)}
                          <br />
                          <small className="text-success" title="Paid to employees">
                            <i className="bi bi-check-circle me-1"></i>
                            {formatCurrency(row.cost_paid || 0)}
                          </small>
                          <small className="text-warning ms-1" title="Not yet paid">
                            <i className="bi bi-clock me-1"></i>
                            {formatCurrency(row.cost_unpaid || 0)}
                          </small>
                        </td>
                        <td className="text-end text-primary">
                          {formatCurrency(row.total_revenue)}
                          <br />
                          <small className="text-success" title="Invoiced to customer">
                            <i className="bi bi-receipt me-1"></i>
                            {formatCurrency(row.revenue_invoiced || 0)}
                          </small>
                          <small className="text-warning ms-1" title="Not yet invoiced">
                            <i className="bi bi-receipt-cutoff me-1"></i>
                            {formatCurrency(row.revenue_not_invoiced || 0)}
                          </small>
                        </td>
                        <td
                          className={`text-end fw-bold ${
                            row.total_profit >= 0
                              ? "text-success"
                              : "text-danger"
                          }`}
                        >
                          {formatCurrency(row.total_profit)}
                        </td>
                        <td className="text-end">
                          <span
                            className={`badge bg-${getMarginColor(row.margin)}`}
                          >
                            {formatNumber(row.margin, 1)}%
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-secondary">
                            {row.consumer_count}
                          </span>
                        </td>
                      </tr>

                      {/* Consumer Rows */}
                      {isUserExpanded &&
                        hasConsumers &&
                        row.consumers.map((consumer) => {
                          const consumerKey = `${row.user_id}-${consumer.consumer_id}`;
                          const isConsumerExpanded =
                            expandedConsumers.has(consumerKey);
                          const hasAuths =
                            consumer.auths && consumer.auths.length > 0;

                          return (
                            <React.Fragment key={consumerKey}>
                              {/* Consumer Row */}
                              <tr
                                className="bg-light"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  hasAuths &&
                                    toggleConsumerExpansion(
                                      row.user_id,
                                      consumer.consumer_id
                                    );
                                }}
                                style={hasAuths ? { cursor: "pointer" } : {}}
                              >
                                <td className="text-center ps-3">
                                  {hasAuths && (
                                    <i
                                      className={`bi bi-chevron-${
                                        isConsumerExpanded ? "down" : "right"
                                      } text-muted`}
                                    ></i>
                                  )}
                                </td>
                                <td className="ps-4">
                                  <i className="bi bi-person me-2 text-muted"></i>
                                  {consumer.consumer_name}
                                </td>
                                <td className="text-end text-muted">
                                  {formatNumber(consumer.total_hours, 1)}
                                </td>
                                <td className="text-end text-muted">
                                  {formatNumber(consumer.total_mileage, 0)}
                                </td>
                                <td></td>
                                <td className="text-end text-muted">
                                  {formatCurrency(consumer.total_cost)}
                                  <br />
                                  <small className="text-success" title="Paid to employees">
                                    <i className="bi bi-check-circle me-1"></i>
                                    {formatCurrency(consumer.cost_paid || 0)}
                                  </small>
                                  <small className="text-warning ms-1" title="Not yet paid">
                                    <i className="bi bi-clock me-1"></i>
                                    {formatCurrency(consumer.cost_unpaid || 0)}
                                  </small>
                                </td>
                                <td className="text-end text-muted">
                                  {formatCurrency(consumer.total_revenue)}
                                  <br />
                                  <small className="text-success" title="Invoiced to customer">
                                    <i className="bi bi-receipt me-1"></i>
                                    {formatCurrency(consumer.revenue_invoiced || 0)}
                                  </small>
                                  <small className="text-warning ms-1" title="Not yet invoiced">
                                    <i className="bi bi-receipt-cutoff me-1"></i>
                                    {formatCurrency(consumer.revenue_not_invoiced || 0)}
                                  </small>
                                </td>
                                <td
                                  className={`text-end ${
                                    consumer.total_profit >= 0
                                      ? "text-success"
                                      : "text-danger"
                                  }`}
                                >
                                  {formatCurrency(consumer.total_profit)}
                                </td>
                                <td className="text-end">
                                  <span
                                    className={`badge bg-${getMarginColor(
                                      consumer.margin
                                    )}`}
                                  >
                                    {formatNumber(consumer.margin, 1)}%
                                  </span>
                                </td>
                                <td className="text-center">
                                  <span className="badge bg-light text-dark">
                                    {consumer.auths?.length || 0}
                                  </span>
                                </td>
                              </tr>

                              {/* Authorization Rows */}
                              {isConsumerExpanded &&
                                hasAuths &&
                                consumer.auths.map((auth, authIndex) => (
                                  <tr
                                    key={`${consumerKey}-${
                                      auth.authorization_id || authIndex
                                    }`}
                                    className="bg-white"
                                  >
                                    <td></td>
                                    <td className="ps-5 text-muted small">
                                      <i className="bi bi-file-text me-2"></i>
                                      {auth.authorization_number}
                                      <span className="ms-2 text-secondary">
                                        ({auth.service_name})
                                      </span>
                                    </td>
                                    <td className="text-end text-muted small">
                                      {formatNumber(auth.hours, 1)}
                                    </td>
                                    <td className="text-end text-muted small">
                                      {formatNumber(auth.mileage, 0)}
                                    </td>
                                    <td></td>
                                    <td className="text-end text-muted small">
                                      {formatCurrency(auth.cost)}
                                      <br />
                                      <small className="text-success" title="Paid to employees">
                                        <i className="bi bi-check-circle me-1"></i>
                                        {formatCurrency(auth.cost_paid || 0)}
                                      </small>
                                      <small className="text-warning ms-1" title="Not yet paid">
                                        <i className="bi bi-clock me-1"></i>
                                        {formatCurrency(auth.cost_unpaid || 0)}
                                      </small>
                                    </td>
                                    <td className="text-end text-muted small">
                                      {formatCurrency(auth.revenue)}
                                      <br />
                                      <small className="text-success" title="Invoiced to customer">
                                        <i className="bi bi-receipt me-1"></i>
                                        {formatCurrency(auth.revenue_invoiced || 0)}
                                      </small>
                                      <small className="text-warning ms-1" title="Not yet invoiced">
                                        <i className="bi bi-receipt-cutoff me-1"></i>
                                        {formatCurrency(auth.revenue_not_invoiced || 0)}
                                      </small>
                                    </td>
                                    <td
                                      className={`text-end small ${
                                        auth.profit >= 0
                                          ? "text-success"
                                          : "text-danger"
                                      }`}
                                    >
                                      {formatCurrency(auth.profit)}
                                    </td>
                                    <td className="text-end">
                                      <span
                                        className={`badge bg-${getMarginColor(
                                          auth.margin
                                        )} small`}
                                      >
                                        {formatNumber(auth.margin, 1)}%
                                      </span>
                                    </td>
                                    <td></td>
                                  </tr>
                                ))}
                            </React.Fragment>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="table-secondary">
                <tr className="fw-bold">
                  <td></td>
                  <td>Totals</td>
                  <td className="text-end">
                    {formatNumber(summary?.total_hours, 1)}
                  </td>
                  <td className="text-end">
                    {formatNumber(summary?.total_mileage, 0)}
                  </td>
                  <td></td>
                  <td className="text-end text-danger">
                    {formatCurrency(summary?.total_cost)}
                    <br />
                    <small className="text-success" title="Paid to employees">
                      <i className="bi bi-check-circle me-1"></i>
                      {formatCurrency(summary?.cost_paid || 0)}
                    </small>
                    <small className="text-warning ms-1" title="Not yet paid">
                      <i className="bi bi-clock me-1"></i>
                      {formatCurrency(summary?.cost_unpaid || 0)}
                    </small>
                  </td>
                  <td className="text-end text-primary">
                    {formatCurrency(summary?.total_revenue)}
                    <br />
                    <small className="text-success" title="Invoiced to customer">
                      <i className="bi bi-receipt me-1"></i>
                      {formatCurrency(summary?.revenue_invoiced || 0)}
                    </small>
                    <small className="text-warning ms-1" title="Not yet invoiced">
                      <i className="bi bi-receipt-cutoff me-1"></i>
                      {formatCurrency(summary?.revenue_not_invoiced || 0)}
                    </small>
                  </td>
                  <td
                    className={`text-end ${
                      summary?.total_profit >= 0
                        ? "text-success"
                        : "text-danger"
                    }`}
                  >
                    {formatCurrency(summary?.total_profit)}
                  </td>
                  <td className="text-end">
                    <span
                      className={`badge bg-${getMarginColor(
                        summary?.avg_margin
                      )}`}
                    >
                      {formatNumber(summary?.avg_margin, 1)}%
                    </span>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* No Data */}
      {!loading && reportData.length === 0 && (
        <div className="text-center py-5">
          <i
            className="bi bi-inbox text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3">No Data Available</h5>
          <p className="text-muted">
            No job coach activity found for the selected period.
          </p>
        </div>
      )}
    </div>
  );
};

export default JobCoachProfitabilityReport;